import { CalendarDays, Info, Sparkles, TrendingUp } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { getCurrencySymbol } from "@/utils/currency";
import type { FareCalculationResult } from "@/types/fareCalculation";

interface VehicleFareResultsProps {
  fareCalculation: FareCalculationResult;
  selectedFare: number;
  onSelectedFareChange: (fare: number) => void;
  seats: number;
  onHowCalculatedClick: () => void;
}

function formatAmount(amount: number): string {
  return amount.toLocaleString("en-IN");
}

export function VehicleFareResults({
  fareCalculation,
  selectedFare,
  onSelectedFareChange,
  seats,
  onHowCalculatedClick,
}: VehicleFareResultsProps) {
  const { perSeatPricing } = fareCalculation;
  const currencySymbol = getCurrencySymbol(perSeatPricing.currency);
  const { minimumFare: minFare, maximumFare: maxFare, suggestedFare } = perSeatPricing;

  const dailySavings = seats * selectedFare;
  const weeklySavings = dailySavings * 5;
  const monthlySavings = weeklySavings * 4;
  const isSuggested = selectedFare === suggestedFare;

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-background p-6 ring-1 ring-primary/15">
        <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-primary/10 blur-2xl" />
        <div className="relative space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary/80">
              Suggested price per seat
            </p>
            <p className="mt-1 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              {currencySymbol}
              {selectedFare.toLocaleString("en-IN")}
              <span className="ml-2 text-lg font-medium text-muted-foreground">
                / seat
              </span>
            </p>
          </div>

          <div className="space-y-3">
            <Slider
              min={minFare}
              max={maxFare}
              step={1}
              value={[selectedFare]}
              onValueChange={([value]) => onSelectedFareChange(value)}
              aria-label="Fare per seat"
              className="py-1"
            />
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-muted-foreground">
                Min {currencySymbol}
                {formatAmount(minFare)}
              </span>
              {!isSuggested && (
                <button
                  type="button"
                  onClick={() => onSelectedFareChange(suggestedFare)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                >
                  <Sparkles className="h-3 w-3" />
                  Reset to AI suggested ({currencySymbol}
                  {formatAmount(suggestedFare)})
                </button>
              )}
              <span className="font-medium text-muted-foreground">
                Max {currencySymbol}
                {formatAmount(maxFare)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border/80 bg-card p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Your potential savings</h3>
        </div>

        <div className="mb-4 rounded-xl bg-primary/5 p-4 ring-1 ring-primary/10">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <CalendarDays className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">Daily (one way)</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {seats} seat{seats !== 1 ? "s" : ""} × {currencySymbol}
                {formatAmount(selectedFare)}
              </p>
              <p className="mt-2 text-2xl font-bold text-primary">
                {currencySymbol} {formatAmount(dailySavings)}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Weekly
            </p>
            <p className="mt-1 text-xs text-muted-foreground">5 working days</p>
            <p className="mt-2 text-xl font-bold text-foreground">
              {currencySymbol} {formatAmount(weeklySavings)}
            </p>
          </div>
          <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Monthly
            </p>
            <p className="mt-1 text-xs text-muted-foreground">4 weeks estimate</p>
            <p className="mt-2 text-xl font-bold text-foreground">
              {currencySymbol} {formatAmount(monthlySavings)}
            </p>
          </div>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="h-11 w-full gap-2 border-primary/20 text-sm font-semibold hover:bg-primary/5"
        onClick={onHowCalculatedClick}
      >
        <Info className="h-4 w-4" />
        How it is calculated
      </Button>
    </div>
  );
}
